import { useContext, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Layout from './components/layout/Layout';
import AuthContext from './contexts/auth-context';
import AppContext from './contexts/app-context';
import AuthPage from './pages/AuthPage/AuthPage';
import RequireAuth from './components/Auth/RequireAuth';
import NotFound from './pages/NotFound/NotFound';
import NewEncounterPage from './pages/NewEncounterPage/NewEncounterPage';
import NewPatient from './pages/NewPatient';
import UpdatePatient from './pages/UpdatePatient';
import MachineLearningPage from './pages/MachineLearningPage';
import DoctorEncounters from './pages/UserProfilePages/DoctorEncounters/DoctorEncounters';
import PatientProfilePage from './pages/UserProfilePages/PatientProfilePage';
import PatientPortalPage from './pages/UserProfilePages/PatientPortal/PatientPortalPage';
import DoctorProfilePage from './pages/UserProfilePages/DoctorProfilePage';
import InsuranceProfilePage from './pages/UserProfilePages/InsuranceProfilePage';
import FacilityProfilePage from './pages/UserProfilePages/FacilityProfilePage';
import PharmacyProfilePage from './pages/UserProfilePages/PharmacyProfilePage';
import InsuranceAnalysisPage from './pages/InsuranceAnalysisPage/InsuranceAnalysisPage';
import FacilityAnalysisPage from './pages/InsuranceAnalysisPage/FacilityAnalysisPagee';
import PatientsPage from './pages/PatientsPage/PatientsPage';
import i18n from './locales/i18n';
import { app_paths } from './components/config/variables'
import CircularProgressComponent from './components/ui/CircularProgressComponent';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Setting from './pages/SettingsPages/Setting';
import DoctorListPage from './pages/DoctorListPage/DoctorListPage';
import NewDoctor from './pages/NewDoctor';
import UpdateDoctor from './pages/UpdateDoctor';
import PricesPage from './pages/PricesPage/PricesPage';
import UploadPricesPage from './pages/PricesPage/UploadPricesPage';
import DoctorApointment from './pages/DoctorApointment/DoctorApointment';
import PharmacyDrugs from './pages/PharmacyDrugs/PharmacyDrugs';
import InventoryPage from './pages/PharmacyDrugs/InventoryPage';
import DispensingRecords from './pages/PharmacyDrugs/DispensingRecords';
import LabQueuedCptsPage from './pages/LabQueuedCptsPage/LabQueuedCptsPage';
import LabTestResults from './pages/LabQueuedCptsPage/LabTestResults';
import LabProfilePage from './pages/UserProfilePages/LabProfilePage';

function App() {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const isLoggedIn = authCtx.isLoggedIn;
  const userRole = appCtx.userRole;
  const language = appCtx.language

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);

  const getRouteForDoctorRole = () => {
    return (
      <Route path="/" element={<Layout />}>
        <Route path="/" element={<DoctorEncounters />} />
        <Route path={app_paths.NEW_PATIENT} element={<NewPatient />} />
        <Route path={app_paths.PATIENTS} element={<PatientsPage />} />
        <Route path={app_paths.UPDATE_PATIENT} element={<UpdatePatient />} />
        <Route path={app_paths.NEW_ENCOUNTER} element={<NewEncounterPage />} />
        <Route path={app_paths.NLP} element={<MachineLearningPage />} />
        <Route path={app_paths.PATIENT_PROFILE} element={<PatientProfilePage />} />
        <Route path={app_paths.DOCTOR_PROFILE} element={<DoctorProfilePage />} />
        <Route path={app_paths.SETTINGS} element={<Setting />} />
        <Route path={app_paths.DOCTOR_APPOINTMENT} element={<DoctorApointment />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  // const getRouteForAdminRole = () => {
  //   return (
  //     <Route path="/" element={<Layout />}>
  //       <Route path="/" element={<AdminProfilePage />} />
  //       <Route path="/patient/:patientID" element={<PatientProfilePage />} />
  //     </Route>
  //   );
  // };

  const getRouteForInsuranceRole = () => {
    return (
      <Route path="/" element={<Layout />}>
        <Route path="/" element={<InsuranceProfilePage />} />
        <Route path={app_paths.NEW_PATIENT} element={<NewPatient />} />
        <Route path={app_paths.PATIENTS} element={<PatientsPage />} />
        <Route path={app_paths.UPDATE_PATIENT} element={<UpdatePatient />} />
        <Route path={app_paths.PATIENT_PROFILE} element={<PatientProfilePage />} />
        <Route path={app_paths.INSURANCE_ANALYSIS} element={<InsuranceAnalysisPage />} />
        <Route path={app_paths.SETTINGS} element={<Setting />} />
        <Route path={app_paths.PRICES_PAGE} element={<PricesPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  const getRouteForFacilityRole = () => {
    return (
      <Route path="/" element={<Layout />}>
        <Route path="/" element={<FacilityProfilePage />} />
        <Route path={app_paths.NEW_DOCTOR} element={<NewDoctor />} />
        <Route path={app_paths.UPDATE_DOCTOR_PROFILE_FOR_FACILITY} element={<UpdateDoctor />} />
        <Route path={app_paths.PATIENTS} element={<PatientsPage />} />
        <Route path={app_paths.DOCTOR_LIST} element={<DoctorListPage />} />
        <Route path={app_paths.DOCTOR_PROFILE_FOR_FACILITY} element={<DoctorProfilePage/>} />
        <Route path={app_paths.PATIENT_PROFILE} element={<PatientProfilePage />} />
        <Route path={app_paths.SETTINGS} element={<Setting />} />
        <Route path={app_paths.INSURANCE_ANALYSIS} element={<FacilityAnalysisPage />} />
        <Route path={app_paths.PRICES_PAGE} element={<PricesPage />} />
        <Route path={app_paths.UPLOAD_PRICES_PAGE} element={<UploadPricesPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  const getRouteForPharmacyRole = () => {
    return (
      <Route path="/" element={<Layout />}>
        <Route path="/" element={<PharmacyDrugs />} />
        <Route path={app_paths.INVENTORY_PAGE} element={<InventoryPage />} />
        <Route path={app_paths.PHARMACY_ENCOUNTER_HISTORY} element={<PharmacyProfilePage />} />
        <Route path={app_paths.PATIENTS} element={<PatientsPage />} />
        <Route path={app_paths.NEW_ENCOUNTER} element={<NewEncounterPage />} />
        <Route path={app_paths.NLP} element={<MachineLearningPage />} />
        <Route path={app_paths.PATIENT_PROFILE} element={<PatientProfilePage />} />
        <Route path={app_paths.DISPENSED_MEDICINES} element={<DispensingRecords />} />
        <Route path={app_paths.SETTINGS} element={<Setting />} />
        <Route path={app_paths.UPLOAD_PRICES_PAGE} element={<UploadPricesPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  const getRouteForLabRole = () => {
    return (
      <Route path="/" element={<Layout />}>
        <Route path="/" element={<LabQueuedCptsPage />} />
        <Route path={app_paths.LAB_TESTS_RESULTS} element={<LabTestResults />} />
        <Route path={app_paths.LAB_ENCOUNTER_HISTORY} element={<LabProfilePage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };


  const getRouteForPatient = () => {
    return (
      <Route path="/" element={<Layout />}>
        <Route path="/" element={<PatientPortalPage />} />
        <Route path={app_paths.SETTINGS} element={<Setting />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    );
  };

  return (
    <>
      <Routes>
        <Route
          path={app_paths.LOGIN_AUTH}
          element={isLoggedIn ? <Navigate to="/" /> : <AuthPage />}
        />
        <Route element={<RequireAuth />}>
          {/* {userRole === "administrator" ? getRouteForAdminRole() : null} */}
          {userRole === "doctor" ? getRouteForDoctorRole() : null}
          {userRole === "insurance_admin" ? getRouteForInsuranceRole() : null}
          {userRole === "facility_admin" ? getRouteForFacilityRole() : null}
          {userRole === "patient" ? getRouteForPatient() : null}
          {userRole === "pharmacy_admin" ? getRouteForPharmacyRole() : null}
          {userRole === "lab_admin" ? getRouteForLabRole() : null}
          {/* it take time to get userRole from the API so show circular progress in this time */}
          <Route path="*" element={<CircularProgressComponent />} />
        </Route>
      </Routes>
      <ToastContainer />
    </>
  );
}

export default App;
